import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '../lib/queryKeys';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';
import { useGoogleBusinessSummary } from './useGoogleBusinessSummary';

export interface OnboardingTask {
  id: string;
  label: string;
  completed: boolean;
  route?: string;
}

export interface OnboardingTasksStatus {
  tasks: OnboardingTask[];
  completedCount: number;
  totalCount: number;
  allCompleted: boolean;
}

/**
 * Hook to check completion status of all onboarding tasks
 */
export function useOnboardingTasks() {
  const { data: companyId, isLoading: isLoadingCompany } = useCurrentCompany();
  const { data: googleBusinessSummary } = useGoogleBusinessSummary();

  return useQuery<OnboardingTasksStatus>({
    queryKey: queryKeys.dashboard.onboardingTasks(companyId),
    enabled: !!companyId && !isLoadingCompany,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!companyId) {
        return {
          tasks: [],
          completedCount: 0,
          totalCount: 0,
          allCompleted: false,
        };
      }

      // Check all tasks in parallel
      const [
        businessProfileResult,
        subscriptionResult,
        discountCodeResult,
        feedPostResult,
        classResult,
        inviteResult,
        enrollmentResult,
        billingResult,
      ] = await Promise.all([
        // 1. Business Profile Complete Including Google Linking
        (async () => {
          let hasGoogleLinked = !!googleBusinessSummary;
          if (!hasGoogleLinked) {
            const { data: companyData } = await supabase
              .from('companies')
              .select('place_id')
              .eq('id', companyId)
              .maybeSingle();
            hasGoogleLinked = !!((companyData as any)?.place_id);
          }
          return hasGoogleLinked;
        })(),
        // 2. Setup Your First Subscription
        supabase
          .from('company_subscriptions')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('is_deleted', false)
          .limit(1)
          .then(({ count }) => (count || 0) > 0),
        // 3. Setup Your First Discount Code
        supabase
          .from('discount_codes')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .limit(1)
          .then(({ count }) => (count || 0) > 0),
        // 4. Make Your First Feed Post
        supabase
          .from('posts')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('visible', true)
          .limit(1)
          .then(({ count }) => (count || 0) > 0),
        // 5. Make Your First Class
        supabase
          .from('calendar_events')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .limit(1)
          .then(({ count }) => (count || 0) > 0),
        // 6. Invite Someone to the Gym (or bulk upload)
        supabase
          .from('draft_students_view')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .not('invite_sent_at', 'is', null)
          .limit(1)
          .then(({ count }) => (count || 0) > 0),
        // 7. Have students connected to subscriptions
        supabase
          .from('subscription_enrollments')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .in('status', ['active', 'pending'])
          .limit(1)
          .then(({ count }) => (count || 0) > 0),
        // 8. Finalize billing settings
        supabase
          .from('subscription_billing_settings')
          .select('id, payment_provider')
          .eq('company_id', companyId)
          .maybeSingle()
          .then(({ data }) => !!(data?.payment_provider)),
      ]);

      const tasks: OnboardingTask[] = [
        {
          id: 'business-profile',
          label: 'Business Profile Complete Including Google Linking',
          completed: businessProfileResult,
          route: '/activity',
        },
        {
          id: 'first-subscription',
          label: 'Setup Your First Subscription',
          completed: subscriptionResult,
          route: '/subscriptions-plans/gym',
        },
        {
          id: 'first-discount-code',
          label: 'Setup Your First Discount Code',
          completed: discountCodeResult,
          route: '/billing/discount-codes',
        },
        {
          id: 'first-feed-post',
          label: 'Make Your First Feed Post',
          completed: feedPostResult,
          route: '/feed',
        },
        {
          id: 'first-class',
          label: 'Make Your First Class',
          completed: classResult,
          route: '/schedule',
        },
        {
          id: 'invite-someone',
          label: 'Invite Someone to the Gym (or bulk upload)',
          completed: inviteResult,
          route: '/upload-student-data',
        },
        {
          id: 'students-connected',
          label: 'Have students connected to subscriptions',
          completed: enrollmentResult,
          route: '/subscriptions-plans/gym',
        },
        {
          id: 'finalize-billing',
          label: 'Finalize billing settings',
          completed: billingResult,
          route: '/billing/settings',
        },
      ];

      const completedCount = tasks.filter(t => t.completed).length;
      const totalCount = tasks.length;
      const allCompleted = completedCount === totalCount;

      return {
        tasks,
        completedCount,
        totalCount,
        allCompleted,
      };
    },
  });
}
